import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface Hotel {
  id: string;
  key: string;
  name: string;
  location: string;
  image: string;
  rating: number;
  reviews: number;
  price: number;
  stars: number;
  checkIn: string;
  checkOut: string;
  roomType: string;
  mealType: string;
  facilities: string[];
  chain?: string;
  webSite?: string;
  description?: string;
}

// Addon Operation Interfaces
export interface AddOnOperation {
  addOnKey: string;
  passengerKey: string;
  type: 'Add' | 'Remove';
}

export interface GetDetailWithOperationsRequest {
  productKey: string;
  traceId?: string; // ⭐ TraceId zorunlu (Backend için)
  include?: {
    hotelDetailItem?: 'All' | 'Basic' | 'Rooms' | 'Prices';
  };
  operations?: AddOnOperation[];
}

export interface AddonInfo {
  key: string;
  id?: number;
  name: string;
  description?: string;
  price?: {
    value: number;
    currency?: number;
  };
  isRequired: boolean;
  isSelected?: boolean;
  passengerKey?: string;
  addonType?: string;
  perPerson?: boolean;
  perNight?: boolean;
}

export interface GetDetailWithOperationsResponse {
  success: boolean;
  traceId?: string;
  productKey?: string;
  message?: string;
  totalPrice?: number | { value: number; currency?: number | string }; // ✅ v3.0: Hem number hem object destekle
  basePrice?: {
    value: number;
    currency?: number | string;
  };
  // ✅ v3.0: Add-On sonrası güncellenen tutar field'ları
  basketLeastwiseAmount?: number | { value: number; currency?: number | string }; // Ödenmesi gereken minimum tutar
  balance?: number | { value: number; currency?: number | string }; // Kalan tutar
  // Backend v3.0: additionalAddons içinde isSelected flag'i ile addon durumları
  additionalAddons?: {
    [passengerKey: string]: AddonInfo[]; // PassengerKey bazlı grouping
  };
  hotel?: any;
  items?: any[];
  roomDetails?: any;
  rawResponse?: string; // v3.0: Manuel SOAP response (debug için)
}

export interface SearchParams {
  location: string;
  checkIn: Date;
  checkOut: Date;
  adults: number;
  children: number;
  rooms: number;
}

@Injectable({
  providedIn: 'root'
})
export class HotelService {
  private apiUrl = environment.apiUrl;
  private searchResults: any;

  // GetDetail cache + in-flight dedup (aynı productKey için 60 sn içinde tekrar istek atma)
  private detailCache = new Map<string, { obs: Observable<any>; ts: number }>();
  private readonly DETAIL_CACHE_TTL = 60_000;

  constructor(private http: HttpClient, private router: Router) {}

  /**
   * Cookie'den TraceId'yi al
   * @returns TraceId string veya null
   */
  public getTraceIdFromCookie(): string | null {
    const cookies = document.cookie.split(';');
    
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      
      if (name === 'TraceId') {
        return value;
      }
    }
    
    return null;
  }

  getHotels(): Observable<any> {
    return of([]);
  }

  getHotelDetails(productKey: string): Observable<any> {
    const cached = this.detailCache.get(productKey);
    if (cached && Date.now() - cached.ts < this.DETAIL_CACHE_TTL) {
      return cached.obs;
    }

    let params = new HttpParams().set('productKey', productKey);
    const traceId = this.getTraceIdFromCookie() || sessionStorage.getItem('lastTraceId');
    if (traceId) params = params.set('traceId', traceId);

    const obs = this.http.get<any>(`${this.apiUrl}/GetDetail`, {
      params,
      withCredentials: true
    }).pipe(
      catchError(error => {
        this.detailCache.delete(productKey);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.detailCache.set(productKey, { obs, ts: Date.now() });
    return obs;
  }

  getPricePeriods(productKey: string): Observable<any> {
    // ✅ TraceId'yi cookie'den al ve kontrol et
    const traceId: string | null = this.getTraceIdFromCookie();
    
    
    if (!traceId) {
    }
    
    const params = new HttpParams().set('productKey', productKey);
    return this.http.post<any>(`${this.apiUrl}/PricePeriods`, null, { 
      params, 
      withCredentials: true 
    });
  }

  getAvailability(productKey: string, roomKey: string): Observable<any> {
    // ✅ TraceId'yi cookie'den al ve kontrol et
    const traceId: string | null = this.getTraceIdFromCookie();
    
    
    if (!traceId) {
    }
    
    const params = new HttpParams()
      .set('productKey', productKey)
      .set('roomKey', roomKey);
    return this.http.get<any>(`${this.apiUrl}/Availability`, { 
      params, 
      withCredentials: true 
    });
  }

  /**
   * Search response'dan ProductKey ve RoomKey değerlerini çıkarır
   * @param searchResponse Search API'den gelen response
   * @returns Array of {productKey, roomKey} objects
   */
  extractKeysFromSearchResponse(searchResponse: any): Array<{productKey: string, roomKey: string}> {
    const keys: Array<{productKey: string, roomKey: string}> = [];
    
    if (searchResponse && searchResponse.items && Array.isArray(searchResponse.items)) {
      searchResponse.items.forEach((item: any) => {
        const productKey = item.key;
        
        if (productKey && item.rooms && Array.isArray(item.rooms)) {
          item.rooms.forEach((room: any) => {
            const roomKey = room.key;
            
            if (roomKey) {
              keys.push({
                productKey: productKey,
                roomKey: roomKey
              });
            }
          });
        }
      });
    }
    
    return keys;
  }

  /**
   * Availability response'unu parse eder ve oda müsaitlik bilgilerini çıkarır
   * @param availabilityData Availability API'den gelen response array
   * @param checkInDate Search check-in tarihi (opsiyonel)
   * @param checkOutDate Search check-out tarihi (opsiyonel)
   * @returns Parsed availability data
   */
  parseAvailabilityData(availabilityData: any[], checkInDate?: Date, checkOutDate?: Date): any[] {
    const parsedData: any[] = [];
    
    availabilityData.forEach((availability: any) => {
      if (availability && availability.result && availability.result.items) {
        Object.keys(availability.result.items).forEach(hotelId => {
          const hotelRooms = availability.result.items[hotelId];
          hotelRooms.forEach((room: any) => {
            let availabilityResult: any;
            if (checkInDate && checkOutDate) {
              availabilityResult = this.checkRoomAvailabilityForDateRange(
                room.availabilityYears, 
                checkInDate, 
                checkOutDate
              );
            } else {
              availabilityResult = this.checkRoomAvailability(room.availabilityYears);
            }
            
            const roomInfo = {
              productKey: availability.productKey,
              roomKey: availability.roomKey,
              hotelId: hotelId,
              roomType: room.roomType,
              roomTypeValue: room.roomTypeValue,
              availabilityYears: room.availabilityYears,
              hasAvailability: availabilityResult.hasAvailability,
              statusSummary: availabilityResult.statusSummary,
              dateRangeDetails: availabilityResult.dateRangeDetails || []
            };
            parsedData.push(roomInfo);
          });
        });
      }
    });
    
    return parsedData;
  }

  /**
   * Oda müsaitlik durumunu kontrol eder
   * @param availabilityYears Yıllık müsaitlik verileri
   * @returns object - Müsaitlik durumu ve detayları
   */
  private checkRoomAvailability(availabilityYears: any[]): {hasAvailability: boolean, statusSummary: any} {
    if (!availabilityYears || availabilityYears.length === 0) {
      return {hasAvailability: false, statusSummary: {available: 0, noQuota: 0, other: 0}};
    }
    
    let hasAvailable = false;
    const statusSummary = {available: 0, noQuota: 0, other: 0};
    
    for (const year of availabilityYears) {
      for (const month of year.availabilityMonths || []) {
        for (const day of month.availabilityDays || []) {
          if (day.statu === 0) {
            hasAvailable = true;
            statusSummary.available++;
          } else if (day.statu === 4) {
            statusSummary.noQuota++;
          } else {
            statusSummary.other++;
          }
        }
      }
    }
    
    return {hasAvailability: hasAvailable, statusSummary};
  }

  /**
   * Belirli tarih aralığında oda müsaitlik durumunu kontrol eder
   * @param availabilityYears Yıllık müsaitlik verileri
   * @param checkInDate Check-in tarihi
   * @param checkOutDate Check-out tarihi
   * @returns object - Belirtilen tarih aralığında müsaitlik durumu ve detayları
   */
  checkRoomAvailabilityForDateRange(availabilityYears: any[], checkInDate: Date, checkOutDate: Date): {hasAvailability: boolean, statusSummary: any, dateRangeDetails: any[]} {
    if (!availabilityYears || availabilityYears.length === 0) {
      return {hasAvailability: false, statusSummary: {available: 0, noQuota: 0, other: 0}, dateRangeDetails: []};
    }
    if (!checkInDate || !checkOutDate) {
      const basicCheck = this.checkRoomAvailability(availabilityYears);
      return {hasAvailability: basicCheck.hasAvailability, statusSummary: basicCheck.statusSummary, dateRangeDetails: []};
    }
    
    const startDate = new Date(checkInDate);
    const endDate = new Date(checkOutDate);
    
    endDate.setDate(endDate.getDate() - 1);
    
    const statusSummary = {available: 0, noQuota: 0, other: 0};
    const dateRangeDetails: any[] = [];
    let allDaysAvailable = true;
    
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const day = currentDate.getDate();
      
      let dayStatus = null;
      let dayAvailable = false;
      
      for (const yearData of availabilityYears) {
        if (yearData.year === year) {
          for (const monthData of yearData.availabilityMonths || []) {
            if (monthData.month === month) {
              for (const dayData of monthData.availabilityDays || []) {
                if (dayData.day === day) {
                  dayStatus = dayData.statu;
                  if (dayData.statu === 0) {
                    dayAvailable = true;
                    statusSummary.available++;
                  } else if (dayData.statu === 4) {
                    statusSummary.noQuota++;
                  } else {
                    statusSummary.other++;
                  }
                  break;
                }
              }
              break;
            }
          }
          break;
        }
      }
      
      dateRangeDetails.push({
        date: new Date(currentDate),
        status: dayStatus,
        available: dayAvailable
      });
      
      if (!dayAvailable) {
        allDaysAvailable = false;
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Sadece TÜM günler müsait ise (status: 0) rezervasyon yapılabilir
    return {hasAvailability: allDaysAvailable, statusSummary, dateRangeDetails};
  }

  searchHotels(params: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/Search`, params, {
      withCredentials: true
    }).pipe(
      tap(response => {
        this.searchResults = response;
      })
    );
  }

  /**
   * SearchResponse'dan temel oda fiyatı ve addon bilgilerini ayrıştırır
   * @param searchResponse Search API'den gelen response
   * @param productKey Otel product key'i
   * @returns Fiyat ayrıştırma bilgileri
   */
  extractPricingDetails(searchResponse: any, productKey: string): any {
    if (!searchResponse || !searchResponse.items) {
      return null;
    }

    // İlgili oteli bul
    const hotelItem = searchResponse.items.find((item: any) => item.key === productKey);
    if (!hotelItem) {
      return null;
    }

    // Hotel bilgilerinden addon'ları al
    const hotelData = searchResponse.hotels?.[hotelItem.hotelKey];
    const addons = hotelData?.addons || [];

    return {
      basePrice: hotelItem.basePrice,
      totalPrice: hotelItem.totalPrice,
      addons: addons,
      rooms: hotelItem.rooms || []
    };
  }

  /**
   * Addon ekleme/çıkarma işlemi (Eski metod - geriye uyumluluk için)
   * @param hotelAddOnRequest Addon request objesi
   * @returns Observable<any>
   * @deprecated GetDetail with Operations kullanın
   */
  manageHotelAddOn(hotelAddOnRequest: any): Observable<any> {
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post<any>(`${this.apiUrl}/HotelAddOn`, hotelAddOnRequest, { 
      headers, 
      withCredentials: true 
    });
  }

  /**
   * AddOn Yönetimi - POST /api/GetDetail endpoint'i
   * @param productKey Ürün anahtarı
   * @param operations Addon işlemleri (Add/Remove)
   * @returns Observable<GetDetailWithOperationsResponse>
   */
  getDetailWithOperations(
    productKey: string, 
    operations: AddOnOperation[]
  ): Observable<GetDetailWithOperationsResponse> {
    // ✅ TraceId'yi cookie'den al
    const traceId: string | null = this.getTraceIdFromCookie();
    
    
    // ⚠️ KRİTİK: TraceId kontrolü
    if (!traceId) {
      return throwError(() => new Error('TraceId cookie bulunamadı. Lütfen önce arama yapın.'));
    }
    
    const request = {
      productKey: productKey,
      traceId: traceId,
      operations: operations
    };

    // ✅ DOĞRU Endpoint: POST /api/GetDetail
    return this.http.post<GetDetailWithOperationsResponse>(
      `${this.apiUrl}/GetDetail`, 
      request, 
      { withCredentials: true }
    ).pipe(
      tap(response => {
        
        // ✅ v3.0: Add-On ekleme teyidi için fiyat field'ları
        if (response.basketLeastwiseAmount !== undefined) {
        }
        if (response.balance !== undefined) {
        }
        
        if (response.rawResponse) {
        }
        if (response.message) {
        }
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /** Tek addon ekleme */
  addAddon(productKey: string, addonKey: string, passengerKey: string): Observable<GetDetailWithOperationsResponse> {
    return this.getDetailWithOperations(productKey, [{ addOnKey: addonKey, passengerKey, type: 'Add' }]);
  }

  /** Tek addon çıkarma */
  removeAddon(productKey: string, addonKey: string, passengerKey: string): Observable<GetDetailWithOperationsResponse> {
    return this.getDetailWithOperations(productKey, [{ addOnKey: addonKey, passengerKey, type: 'Remove' }]);
  }

  /**
   * ⭐ AddOn İşlemleri ile ProductKey Yönetimi
   * 
   * AKIŞ:
   * 1. İlk çağrı: RoomGroup.Key (GetDetail response'ından items[0].key) gönderilir
   * 2. Response: YENİ ProductKey döner (rawResponse'dan parse edilir)
   * 3. Sonraki çağrılar: Bu YENİ ProductKey kullanılır
   * 
   * @param request GetDetailWithOperations request objesi
   * @returns Observable<GetDetailWithOperationsResponse> - Güncellenmiş ProductKey ile
   */
  manageAddOn(request: GetDetailWithOperationsRequest): Observable<GetDetailWithOperationsResponse> {
    // ✅ TraceId'yi önce request'ten al, yoksa cookie'den al
    let traceId: string | null | undefined = request.traceId;
    
    if (!traceId) {
      traceId = this.getTraceIdFromCookie(); // string | null döner
    } else {
    }
    
    
    // ⚠️ KRİTİK: TraceId kontrolü
    if (!traceId) {
      return throwError(() => new Error('TraceId bulunamadı. Lütfen önce arama yapın.'));
    }
    
    // ⭐ TraceId'yi request'e ekle/güncelle
    const fullRequest = {
      ...request,
      traceId: traceId // Request'ten ya da cookie'den gelen TraceId
    };
    
    
    return this.http.post<GetDetailWithOperationsResponse>(
      `${this.apiUrl}/GetDetail`,
      fullRequest, // ⭐ TraceId dahil request
      { withCredentials: true }
    ).pipe(
      map(response => {
        let newProductKey = response.productKey; // Backend'den gelen ProductKey (fallback)
        
        // ⭐ rawResponse içinden YENİ ProductKey'i parse et (RoomGroup.Key)
        if (response.rawResponse) {
          const keyMatch = response.rawResponse.match(
            /<RoomGroup[^>]*>[\s\S]*?<Key[^>]*>([^<]+)<\/Key>/i
          );
          
          if (keyMatch && keyMatch[1]) {
            newProductKey = keyMatch[1].trim();
          } else {
          }
        } else {
        }
        
        // ⭐ Response'u güncellenmiş ProductKey ile döndür
        return {
          ...response,
          productKey: newProductKey  // YENİ ProductKey
        };
      }),
      tap(response => {
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

}